import { useCallback } from 'react';
import { type StripeError } from '@stripe/stripe-js';
import { isStripeElementPaymentMethod, type PaymentMethod } from '../types';
import {
    usePaymentMethodSelectContext,
    type CreditCardForm,
    type AchFormErrors,
} from './PaymentMethodSelectContext';

import { useStripeSubmit } from './stripe/submitStripePaymentMethod';
import { useCreditCardSubmit } from './creditCard/submitCreditCard';
import { useAchSubmit } from './ach/submitAch';
import { usePayPalSubmit } from './payPal/submitPayPalPaymentMethod';

type SubmitErrorType = 'VALIDATION' | 'IMPLEMENTATION' | 'VAULT';
export type SubmitError = {
    type: SubmitErrorType;
    message: string;
    stripeError?: StripeError;
    payPalError?: unknown;
    creditCardErrors?: CreditCardForm;
    achErrors?: AchFormErrors;
};

export type SubmitResult = {
    paymentMethod?: PaymentMethod | null;
    error?: SubmitError;
};

export function createSubmitError(
    type: SubmitErrorType,
    message: string,
    additional: {
        stripeError?: StripeError;
        payPalError?: unknown;
        creditCardErrors?: CreditCardForm;
        achErrors?: AchFormErrors;
    } = {}
): SubmitResult {
    return { error: { type, message, ...additional } };
}

export function usePaymentMethodSubmit(): () => Promise<SubmitResult> {
    const { selectedPaymentMethod } = usePaymentMethodSelectContext();

    const submitStripe = useStripeSubmit();
    const submitCreditCard = useCreditCardSubmit();
    const submitAch = useAchSubmit();
    const submitPayPal = usePayPalSubmit();

    return useCallback(async () => {
        if (!selectedPaymentMethod) {
            return createSubmitError(
                'IMPLEMENTATION',
                'PaymentMethodSelect submitted without selected payment method'
            );
        }

        const { paymentMethodType, paymentMethodToken } = selectedPaymentMethod;

        if (isStripeElementPaymentMethod(selectedPaymentMethod) && !paymentMethodToken) {
            return await submitStripe(selectedPaymentMethod);
        } else {
            switch (paymentMethodType) {
                case 'CREDIT_CARD':
                    return await submitCreditCard(selectedPaymentMethod);
                case 'ACH':
                    return await submitAch(selectedPaymentMethod);
                case 'PAYPAL_ACCOUNT':
                    return await submitPayPal(selectedPaymentMethod);
                case 'WIRE_TRANSFER':
                case 'ON_PLATFORM_WIRE_TRANSFER':
                case 'APPLE_PAY':
                    return { paymentMethod: selectedPaymentMethod };
                default:
                    return createSubmitError(
                        'IMPLEMENTATION',
                        `submitting ${paymentMethodType} is not implemented yet`
                    );
            }
        }
    }, [selectedPaymentMethod, submitStripe, submitCreditCard, submitAch, submitPayPal]);
}
